import { useState, useRef, useEffect, forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { cn } from '../../lib/utils';
import { QRCodeSkeleton } from './Skeleton';

interface LazyQRCodeProps {
  value: string;
  size?: number;
  level?: 'L' | 'M' | 'Q' | 'H';
  includeMargin?: boolean;
  className?: string;
  skeleton?: React.ReactNode;
  onLoad?: () => void;
}

export const LazyQRCode = forwardRef<SVGSVGElement, LazyQRCodeProps>(
  (
    { value, size = 192, level = 'M', includeMargin = true, className, skeleton, onLoad, ...props },
    ref
  ) => {
    const [isInView, setIsInView] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Intersection Observer for lazy loading
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1, rootMargin: '50px' }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }, []);

    // Handle QR code load
    useEffect(() => {
      if (isInView && value) {
        // Small delay to simulate loading time and show skeleton
        const timer = setTimeout(() => {
          setIsLoaded(true);
          onLoad?.();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isInView, value, onLoad]);

    return (
      <div
        ref={containerRef}
        className={cn('relative', className)}
        style={{ width: size, height: size }}
      >
        {/* Show skeleton while loading */}
        {!isLoaded && (skeleton || <QRCodeSkeleton />)}

        {/* Render QR code when in view and loaded */}
        {isInView && isLoaded && (
          <QRCodeSVG
            ref={ref}
            value={value}
            size={size}
            level={level}
            includeMargin={includeMargin}
            className={cn(
              'transition-opacity duration-300 opacity-0 animate-fade-in',
              'opacity-100'
            )}
            {...props}
          />
        )}
      </div>
    );
  }
);

LazyQRCode.displayName = 'LazyQRCode';
